import React from 'react'
import { PartnerRecruitment } from '../../clients/kyousou_api_client'
import BaseCard from './base_card'
import LoadingSpinner from '../loading/loading_spinner'
import { SDGsCategoryLabel } from '../../enums/SDGsCategory'
import { formatDate } from '../../utils/datetime_util'
import FavoriteButton from '../buttons/favorite_button'
import { useEnterpriseFavorite } from '../../hooks/use_enterprise_favorite'

interface PartnerRecruitmentCardProps {
  recruitment: PartnerRecruitment
  initialized: boolean
  onClick?: () => void
}

const PartnerRecruitmentCard: React.FC<PartnerRecruitmentCardProps> = ({
  recruitment,
  initialized,
  onClick,
}) => {
  const { isFavorited, toggleFavorite } = useEnterpriseFavorite(
    recruitment.enterprise.id,
    recruitment.enterprise.isFavorited
  )

  // HTMLタグを除去してプレーンテキストに変換し、3行に制限
  const getDescriptionPreview = (htmlContent: string) => {
    // HTMLタグを除去
    const textContent = htmlContent.replace(/<[^>]*>/g, '')
    // 改行で分割して最初の3行を取得
    const lines = textContent
      .split('\n')
      .filter((line) => line.trim().length > 0)
    const previewLines = lines.slice(0, 3)

    // 3行より多い場合は...を追加
    if (lines.length > 3) {
      return previewLines.join('\n') + '...'
    }
    return previewLines.join('\n')
  }
  return (
    <BaseCard onClick={onClick}>
      <>
        {initialized ? (
          <>
            <div className="flex flex-col h-full">
              {recruitment.thumbnail_url ? (
                <img
                  src={recruitment.thumbnail_url}
                  alt="募集サムネイル"
                  className="w-full h-48 object-cover rounded mb-3 bg-main-gray"
                />
              ) : (
                <div className="w-full h-48 bg-main-gray rounded mb-3 flex items-center justify-center">
                  <span className="material-icons !text-6xl text-main-darkgray">
                    image
                  </span>
                </div>
              )}
              <h3 className="font-bold text-lg mb-1">{recruitment.title}</h3>
              <div className="text-xs text-main-darkgray mb-1 flex items-center gap-1">
                <span className="material-icons !text-base align-middle">
                  calendar_today
                </span>
                公開日：
                {formatDate(recruitment.created_at)}
              </div>
              <p className="text-sm mb-3 whitespace-pre-line line-clamp-3">
                {getDescriptionPreview(recruitment.description || '')}
              </p>
              <div className="flex flex-wrap gap-2">
                {recruitment.sdgs_categories?.map((cat, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 rounded-full border border-main-green text-main-green text-xs font-bold bg-main-white"
                  >
                    目標 {cat}: {SDGsCategoryLabel[cat] || ''}
                  </span>
                ))}
              </div>

              {/* 企業情報 */}
              <div className="mt-auto">
                {/* 黒バー */}
                <div className="border-b border-main-black my-4"></div>

                <div className="flex items-center gap-2">
                  <img
                    src={recruitment.enterprise.logoUrl}
                    alt="企業ロゴ"
                    className="w-12 h-12 rounded object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold line-clamp-1">
                      {recruitment.enterprise?.name}
                    </p>
                  </div>
                  {/* お気に入りボタン */}
                  <div className="flex-shrink-0">
                    <FavoriteButton
                      isFavorited={isFavorited}
                      isOwn={recruitment.enterprise.isOwn}
                      onClick={toggleFavorite}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <LoadingSpinner className="my-20" />
        )}
      </>
    </BaseCard>
  )
}

export default PartnerRecruitmentCard
